import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Modal, Table, message } from 'antd';
import {
  DownloadOutlined,
  CodeOutlined,
  MoreOutlined,
  CloseOutlined,
  FundOutlined,
  StockOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  HeartOutlined,
  BarChartOutlined,
  PlusOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import {
  unwrapCasPayload,
  summarizeCas,
  formatInr,
  formatQty,
  formatPct,
  formatPeriod,
  formatDate,
  amcColor,
  includesFilter,
  downloadCsv,
  downloadJson,
} from './casImportUtils';
import { ISIN_PATTERN } from '../../../services/saveEnginePortfolio';
import apiClient from '../../../services/apiClient';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';
import { portfolioAuthHeaders } from './usePortfolioUser';
import './ImportedCasPortfolio.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const CAS_IMPORT_PORTFOLIO_TYPE = 'REGULAR_INCOME';

const TABS = [
  { key: 'mf', label: 'Mutual Funds', icon: <FundOutlined /> },
  { key: 'demat', label: 'Demat', icon: <StockOutlined /> },
  { key: 'bonds', label: 'Bonds', icon: <BankOutlined /> },
  { key: 'nps', label: 'NPS', icon: <SafetyCertificateOutlined /> },
  { key: 'insurance', label: 'Insurance', icon: <HeartOutlined /> },
  { key: 'analytics', label: 'Analytics', icon: <BarChartOutlined />, beta: true },
];

function issuerFromBondRow(row) {
  const isin = String(row?.isin || '').trim().toUpperCase();
  if (row?.type === 'G-Sec' || /^IN0/.test(isin)) {
    return 'Government of India';
  }
  const name = String(row?.name || '').trim();
  const beforePct = name.split(/\d+(?:\.\d+)?\s*%/)[0].trim();
  return beforePct || name || isin;
}

function couponFromBondRow(row) {
  if (row?.couponRate != null && Number.isFinite(Number(row.couponRate))) {
    return Number(row.couponRate);
  }
  const match = String(row?.name || '').match(/(\d+(?:\.\d+)?)\s*%/);
  return match ? Number(match[1]) : undefined;
}

function buildBondUpsertPayload(row) {
  const qtyRaw = Number(row.units);
  const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
  const value = Number(row.value) || 0;
  const price = value > 0
    ? Number((value / (quantity * 10)).toFixed(4))
    : 100;

  return {
    quantity,
    price,
    type: CAS_IMPORT_PORTFOLIO_TYPE,
    issuer: issuerFromBondRow(row),
    maturityDate: row.maturityDate || undefined,
    coupon: couponFromBondRow(row),
  };
}

function signedInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return formatInr(value);
  const formatted = formatInr(Math.abs(n));
  return n > 0 ? `+${formatted}` : `-${formatted}`;
}

function GainText({ value, pct }) {
  const n = Number(value);
  if (!Number.isFinite(n)) return <span>—</span>;
  const cls = n >= 0 ? 'cas-gain' : 'cas-loss';
  return (
    <span className={cls}>
      {signedInr(n)}
      {pct != null && Number.isFinite(Number(pct)) ? ` (${formatPct(pct)})` : ''}
    </span>
  );
}

function TypeTag({ type }) {
  const key = String(type || 'other').toLowerCase().replace(/\s+/g, '-');
  const cls = ['equity', 'debt', 'hybrid', 'corporate-bond', 'g-sec'].includes(key)
    ? (key === 'corporate-bond' || key === 'g-sec' ? 'debt' : key)
    : 'other';
  return <span className={`cas-type-tag ${cls}`}>{type || 'Other'}</span>;
}

function AmcCell({ name }) {
  const letter = String(name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <span className="cas-amc">
      <span className="cas-amc-mark" style={{ background: amcColor(name) }}>{letter}</span>
      <span className="cas-amc-name">{name}</span>
    </span>
  );
}

function textFilter(placeholder) {
  return ({ setSelectedKeys, selectedKeys, confirm }) => (
    <div style={{ padding: 8, width: 200 }}>
      <Input
        allowClear
        size="small"
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={(e) => {
          const next = e.target.value ? [e.target.value] : [];
          setSelectedKeys(next);
          confirm({ closeDropdown: false });
        }}
        onPressEnter={() => confirm()}
      />
    </div>
  );
}

function HistoryButton({ transactions }) {
  const [open, setOpen] = useState(false);
  if (!transactions?.length) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <>
      <button type="button" className="cas-history-btn" onClick={() => setOpen(true)} aria-label="View history">
        <MoreOutlined />
      </button>
      <Modal
        title="Transaction history"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={720}
      >
        <Table
          size="small"
          pagination={false}
          rowKey={(_, i) => String(i)}
          dataSource={transactions}
          columns={[
            { title: 'Date', dataIndex: 'date', render: formatDate, width: 120 },
            { title: 'Type', dataIndex: 'type', width: 140 },
            { title: 'Description', dataIndex: 'description' },
            { title: 'Units', dataIndex: 'units', align: 'right', render: (v) => formatQty(v) },
            { title: 'Amount', dataIndex: 'amount', align: 'right', render: (v) => formatInr(v) },
          ]}
        />
      </Modal>
    </>
  );
}

function MutualFundsTable({ rows }) {
  const columns = [
    {
      title: 'AMC',
      dataIndex: 'amc',
      width: 170,
      filtered: true,
      filterDropdown: textFilter('Filter AMC'),
      onFilter: (q, row) => includesFilter(row.amc, q),
      render: (v) => <AmcCell name={v} />,
    },
    {
      title: 'Scheme',
      dataIndex: 'scheme',
      ellipsis: true,
      filterDropdown: textFilter('Filter scheme'),
      onFilter: (q, row) => includesFilter(row.scheme, q),
    },
    {
      title: 'Folio',
      dataIndex: 'folio',
      width: 130,
      filterDropdown: textFilter('Filter folio'),
      onFilter: (q, row) => includesFilter(row.folio, q),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 100,
      filters: [...new Set(rows.map((r) => r.type))].map((t) => ({ text: t, value: t })),
      onFilter: (v, row) => row.type === v,
      render: (v) => <TypeTag type={v} />,
    },
    { title: 'Units', dataIndex: 'units', width: 100, align: 'right', sorter: (a, b) => (a.units || 0) - (b.units || 0), render: (v) => <span className="cas-num">{formatQty(v)}</span> },
    { title: 'NAV', dataIndex: 'nav', width: 100, align: 'right', render: (v) => <span className="cas-num">{formatQty(v)}</span> },
    { title: 'Invested', dataIndex: 'invested', width: 120, align: 'right', sorter: (a, b) => (a.invested || 0) - (b.invested || 0), render: (v) => <span className="cas-num">{formatInr(v)}</span> },
    { title: 'Current', dataIndex: 'current', width: 120, align: 'right', sorter: (a, b) => (a.current || 0) - (b.current || 0), render: (v) => <span className="cas-num">{formatInr(v)}</span> },
    { title: 'Gain/Loss', dataIndex: 'gain', width: 130, align: 'right', sorter: (a, b) => (a.gain || 0) - (b.gain || 0), render: (v) => <GainText value={v} /> },
    { title: 'Return', dataIndex: 'returnPct', width: 100, align: 'right', sorter: (a, b) => (a.returnPct || 0) - (b.returnPct || 0), render: (v) => <span className={Number(v) >= 0 ? 'cas-gain cas-num' : 'cas-loss cas-num'}>{formatPct(v)}</span> },
    { title: 'History', key: 'history', width: 80, align: 'center', render: (_, row) => <HistoryButton transactions={row.transactions} /> },
  ];

  return (
    <Table
      className="cas-table"
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={rows}
      pagination={rows.length > 12 ? { pageSize: 12, showSizeChanger: false } : false}
      scroll={{ x: 1280 }}
    />
  );
}

function DematTable({ rows }) {
  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      width: 140,
      filters: [...new Set(rows.map((r) => r.type))].map((t) => ({ text: t, value: t })),
      onFilter: (v, row) => row.type === v,
      render: (v) => <TypeTag type={v} />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      ellipsis: true,
      filterDropdown: textFilter('Filter name'),
      onFilter: (q, row) => includesFilter(row.name, q),
    },
    {
      title: 'ISIN',
      dataIndex: 'isin',
      width: 140,
      filterDropdown: textFilter('Filter ISIN'),
      onFilter: (q, row) => includesFilter(row.isin, q),
    },
    { title: 'DP', dataIndex: 'dp', width: 160, ellipsis: true },
    { title: 'Account', dataIndex: 'account', width: 150 },
    { title: 'Units', dataIndex: 'units', width: 110, align: 'right', render: (v) => <span className="cas-num">{formatQty(v)}</span> },
    { title: 'Value', dataIndex: 'value', width: 130, align: 'right', sorter: (a, b) => (a.value || 0) - (b.value || 0), render: (v) => <span className="cas-num">{formatInr(v)}</span> },
    { title: 'History', key: 'history', width: 80, align: 'center', render: (_, row) => <HistoryButton transactions={row.transactions} /> },
  ];

  return (
    <Table
      className="cas-table"
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={rows}
      pagination={rows.length > 12 ? { pageSize: 12, showSizeChanger: false } : false}
      scroll={{ x: 1100 }}
    />
  );
}

function BondsTable({ rows, onAddBond, addingIsin, addedIsins }) {
  const [scanByIsin, setScanByIsin] = useState({});

  useEffect(() => {
    let cancelled = false;
    const isins = [...new Set(
      (rows || [])
        .map((row) => String(row.isin || '').trim().toUpperCase())
        .filter((isin) => ISIN_PATTERN.test(isin))
    )];

    if (!isins.length) {
      setScanByIsin({});
      return undefined;
    }

    setScanByIsin((prev) => {
      const next = { ...prev };
      isins.forEach((isin) => {
        if (next[isin] === undefined) next[isin] = { loading: true, score: null };
      });
      return next;
    });

    (async () => {
      const entries = await Promise.all(isins.map(async (isin) => {
        try {
          const res = await apiClient.get(`${BASE_URL}/scan/details/${encodeURIComponent(isin)}`, {
            timeout: 15000,
          });
          const scoreRaw = res?.data?.totalWeightedScore ?? res?.data?.data?.totalWeightedScore;
          const score = scoreRaw == null || scoreRaw === '' ? null : Number(scoreRaw);
          return [isin, { loading: false, score: Number.isFinite(score) ? score : null }];
        } catch {
          return [isin, { loading: false, score: null }];
        }
      }));
      if (cancelled) return;
      setScanByIsin((prev) => {
        const next = { ...prev };
        entries.forEach(([isin, value]) => {
          next[isin] = value;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [rows]);

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      width: 150,
      filters: [...new Set(rows.map((r) => r.type))].map((t) => ({ text: t, value: t })),
      onFilter: (v, row) => row.type === v,
      render: (v) => <TypeTag type={v} />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      ellipsis: true,
      filterDropdown: textFilter('Filter name'),
      onFilter: (q, row) => includesFilter(row.name, q),
    },
    {
      title: 'ISIN',
      dataIndex: 'isin',
      width: 140,
      filterDropdown: textFilter('Filter ISIN'),
      onFilter: (q, row) => includesFilter(row.isin, q),
    },
    {
      title: 'Coupon',
      dataIndex: 'couponRate',
      width: 100,
      align: 'right',
      sorter: (a, b) => (a.couponRate || 0) - (b.couponRate || 0),
      render: (v) => (v == null ? '—' : <span className="cas-num">{Number(v).toFixed(2)}%</span>),
    },
    {
      title: 'Maturity',
      dataIndex: 'maturityDate',
      width: 120,
      render: (v) => formatDate(v) || '—',
    },
    { title: 'Units', dataIndex: 'units', width: 100, align: 'right', render: (v) => <span className="cas-num">{formatQty(v)}</span> },
    { title: 'Value', dataIndex: 'value', width: 130, align: 'right', sorter: (a, b) => (a.value || 0) - (b.value || 0), render: (v) => <span className="cas-num">{formatInr(v)}</span> },
    {
      title: 'Scan',
      key: 'scan',
      width: 100,
      align: 'right',
      sorter: (a, b) => {
        const aScore = scanByIsin[String(a.isin || '').trim().toUpperCase()]?.score;
        const bScore = scanByIsin[String(b.isin || '').trim().toUpperCase()]?.score;
        return (aScore ?? -1) - (bScore ?? -1);
      },
      render: (_, row) => {
        const isin = String(row.isin || '').trim().toUpperCase();
        const entry = scanByIsin[isin];
        if (!entry || entry.loading) {
          return <span className="cas-muted">…</span>;
        }
        if (entry.score == null) {
          return <span className="cas-muted">—</span>;
        }
        const score = entry.score;
        const tone = score <= 40 ? 'low' : score <= 70 ? 'mid' : 'high';
        return <span className={`cas-scan-score cas-scan-score--${tone}`}>{score.toFixed(1)}</span>;
      },
    },
    { title: 'History', key: 'history', width: 80, align: 'center', render: (_, row) => <HistoryButton transactions={row.transactions} /> },
    {
      title: 'Portfolio',
      key: 'add',
      width: 110,
      align: 'center',
      fixed: 'right',
      render: (_, row) => {
        const isin = String(row.isin || '').trim().toUpperCase();
        const added = addedIsins?.has(isin);
        const busy = addingIsin === isin;
        return (
          <button
            type="button"
            className={`cas-add-btn${added ? ' cas-add-btn--added' : ''}`}
            disabled={busy || added || !ISIN_PATTERN.test(isin)}
            onClick={() => onAddBond?.(row)}
          >
            {busy ? '…' : added ? <><CheckOutlined /> Added</> : <><PlusOutlined /> Add</>}
          </button>
        );
      },
    },
  ];

  return (
    <Table
      className="cas-table"
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={rows}
      pagination={rows.length > 12 ? { pageSize: 12, showSizeChanger: false } : false}
      scroll={{ x: 1200 }}
    />
  );
}

function NpsTable({ rows }) {
  return (
    <Table
      className="cas-table"
      size="small"
      rowKey="key"
      dataSource={rows}
      pagination={false}
      scroll={{ x: 1000 }}
      columns={[
        { title: 'PRAN', dataIndex: 'pran', width: 140 },
        { title: 'Fund', dataIndex: 'fund', ellipsis: true },
        { title: 'CRA', dataIndex: 'cra', width: 100 },
        { title: 'Units', dataIndex: 'units', width: 110, align: 'right', render: (v) => formatQty(v) },
        { title: 'NAV', dataIndex: 'nav', width: 100, align: 'right', render: (v) => formatQty(v) },
        { title: 'Invested', dataIndex: 'invested', width: 120, align: 'right', render: (v) => formatInr(v) },
        { title: 'Current', dataIndex: 'current', width: 120, align: 'right', render: (v) => formatInr(v) },
        { title: 'Gain/Loss', dataIndex: 'gain', width: 120, align: 'right', render: (v) => <GainText value={v} /> },
      ]}
    />
  );
}

function InsuranceTable({ rows }) {
  return (
    <Table
      className="cas-table"
      size="small"
      rowKey="key"
      dataSource={rows}
      pagination={false}
      scroll={{ x: 1000 }}
      columns={[
        { title: 'Provider', dataIndex: 'provider' },
        { title: 'Policy', dataIndex: 'policy', ellipsis: true },
        { title: 'Number', dataIndex: 'number', width: 140 },
        { title: 'Life assured', dataIndex: 'lifeAssured', width: 140 },
        { title: 'Status', dataIndex: 'status', width: 100 },
        { title: 'Sum assured', dataIndex: 'sumAssured', width: 130, align: 'right', render: (v) => formatInr(v) },
        { title: 'Premium', dataIndex: 'premium', width: 120, align: 'right', render: (v) => formatInr(v) },
        { title: 'Frequency', dataIndex: 'frequency', width: 110 },
      ]}
    />
  );
}

function AllocationBars({ title, items }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  if (!items.length) return null;
  return (
    <div className="cas-analytics-card">
      <h3>{title}</h3>
      {items.map((item) => (
        <div className="cas-bar-row" key={item.label}>
          <span className="cas-bar-label">{item.label}</span>
          <div className="cas-bar-track">
            <div className="cas-bar-fill" style={{ width: `${(item.value / max) * 100}%` }} />
          </div>
          <span className="cas-num">{formatInr(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

function AnalyticsPanel({ stats }) {
  const byClass = [
    { label: 'Mutual Funds', value: stats.mfCurrent },
    { label: 'Demat', value: stats.dematValue },
    { label: 'Bonds', value: stats.bondValue },
    { label: 'NPS', value: stats.npsValue },
    { label: 'Insurance', value: stats.insuranceValue },
  ].filter((i) => i.value > 0);

  const byType = Object.values(
    stats.mfRows.reduce((acc, row) => {
      const key = row.type || 'Other';
      acc[key] = acc[key] || { label: key, value: 0 };
      acc[key].value += row.current || 0;
      return acc;
    }, {}),
  ).sort((a, b) => b.value - a.value);

  const byAmc = Object.values(
    stats.mfRows.reduce((acc, row) => {
      const key = row.amc || 'Other';
      acc[key] = acc[key] || { label: key, value: 0 };
      acc[key].value += row.current || 0;
      return acc;
    }, {}),
  ).sort((a, b) => b.value - a.value).slice(0, 8);

  return (
    <div className="cas-analytics-grid">
      <AllocationBars title="Value by asset class" items={byClass} />
      <AllocationBars title="Mutual funds by type" items={byType} />
      <AllocationBars title="Top AMCs by current value" items={byAmc} />
    </div>
  );
}

function exportCurrentTab(tab, stats, root) {
  if (tab === 'mf') {
    downloadCsv('mutual-funds.csv', [
      { label: 'AMC', get: (r) => r.amc },
      { label: 'Scheme', get: (r) => r.scheme },
      { label: 'Folio', get: (r) => r.folio },
      { label: 'Type', get: (r) => r.type },
      { label: 'Units', get: (r) => r.units },
      { label: 'NAV', get: (r) => r.nav },
      { label: 'Invested', get: (r) => r.invested },
      { label: 'Current', get: (r) => r.current },
      { label: 'Gain', get: (r) => r.gain },
      { label: 'Return %', get: (r) => r.returnPct },
      { label: 'ISIN', get: (r) => r.isin },
    ], stats.mfRows);
    return;
  }
  if (tab === 'demat') {
    downloadCsv('demat-holdings.csv', [
      { label: 'Type', get: (r) => r.type },
      { label: 'Name', get: (r) => r.name },
      { label: 'ISIN', get: (r) => r.isin },
      { label: 'DP', get: (r) => r.dp },
      { label: 'Account', get: (r) => r.account },
      { label: 'Units', get: (r) => r.units },
      { label: 'Value', get: (r) => r.value },
    ], stats.dematRows);
    return;
  }
  if (tab === 'bonds') {
    downloadCsv('bonds.csv', [
      { label: 'Type', get: (r) => r.type },
      { label: 'Name', get: (r) => r.name },
      { label: 'ISIN', get: (r) => r.isin },
      { label: 'Coupon %', get: (r) => r.couponRate },
      { label: 'Maturity', get: (r) => r.maturityDate },
      { label: 'Units', get: (r) => r.units },
      { label: 'Value', get: (r) => r.value },
    ], stats.bondRows);
    return;
  }
  if (tab === 'nps') {
    downloadCsv('nps.csv', [
      { label: 'PRAN', get: (r) => r.pran },
      { label: 'Fund', get: (r) => r.fund },
      { label: 'Units', get: (r) => r.units },
      { label: 'Current', get: (r) => r.current },
    ], stats.npsRows);
    return;
  }
  if (tab === 'insurance') {
    downloadCsv('insurance.csv', [
      { label: 'Provider', get: (r) => r.provider },
      { label: 'Policy', get: (r) => r.policy },
      { label: 'Number', get: (r) => r.number },
      { label: 'Sum assured', get: (r) => r.sumAssured },
    ], stats.insuranceRows);
    return;
  }
  downloadJson('portfolio.json', root);
}

export default function ImportedCasPortfolio({ data, metadata, isDark, onClear, userId }) {
  const root = useMemo(() => unwrapCasPayload(data), [data]);
  const stats = useMemo(() => summarizeCas(root), [root]);
  const [tab, setTab] = useState(() => {
    if (stats.mfCount) return 'mf';
    if (stats.bondCount) return 'bonds';
    if (stats.dematCount) return 'demat';
    if (stats.npsCount) return 'nps';
    if (stats.insuranceCount) return 'insurance';
    return 'analytics';
  });
  const [jsonOpen, setJsonOpen] = useState(false);
  const [addingIsin, setAddingIsin] = useState('');
  const [addedIsins, setAddedIsins] = useState(() => new Set());

  const handleAddBond = useCallback(async (row) => {
    const isin = String(row?.isin || '').trim().toUpperCase();
    if (!ISIN_PATTERN.test(isin)) {
      message.error('This holding does not have a valid ISIN.');
      return;
    }
    if (!userId) {
      message.warning('Please log in to add bonds to your portfolio.');
      return;
    }

    setAddingIsin(isin);
    try {
      const payload = buildBondUpsertPayload(row);
      await apiClient.post(`${BASE_URL}/user/portfolio/${isin}`, payload, {
        headers: portfolioAuthHeaders(userId),
      });
      setAddedIsins((prev) => new Set(prev).add(isin));
      message.success(`${isin} added to My Portfolio`);
    } catch (err) {
      message.error(getApiErrorMessage(err, 'Failed to add bond to portfolio'));
    } finally {
      setAddingIsin('');
    }
  }, [userId]);

  const investor = root.investor || root.investor_info || {};
  const meta = root.meta || {};
  const casType = meta.cas_type || root.cas_type || metadata?.cas_type || metadata?.parser_type;
  const period = formatPeriod(meta.statement_period?.from, meta.statement_period?.to);
  const casId = investor.cas_id;

  const counts = {
    mf: stats.mfCount,
    demat: stats.dematCount,
    bonds: stats.bondCount,
    nps: stats.npsCount,
    insurance: stats.insuranceCount,
  };

  const toolbarCopy = {
    mf: `${stats.mfCount} schemes • Invested ${formatInr(stats.mfInvested)} • Current ${formatInr(stats.mfCurrent)} • `,
    demat: `${stats.dematCount} holdings • Current ${formatInr(stats.dematValue)}`,
    bonds: `${stats.bondCount} bonds • Current ${formatInr(stats.bondValue)}`,
    nps: stats.npsCount ? `${stats.npsCount} funds • Current ${formatInr(stats.npsValue)}` : 'No NPS accounts in this statement',
    insurance: stats.insuranceCount ? `${stats.insuranceCount} policies` : 'No insurance policies in this statement',
    analytics: 'Allocation across imported asset classes',
  };

  return (
    <section className={`cas-import${isDark ? ' cas-import--dark' : ''}`}>
      <div className="cas-import-panel">
        <div className="cas-import-top">
          <div className="cas-import-identity">
            <h2 className="cas-import-name">{investor.name || 'Imported portfolio'}</h2>
            <p className="cas-import-meta">
              {investor.pan && <span>{investor.pan}</span>}
              {casType && (
                <>
                  {investor.pan && <span className="cas-import-dot">·</span>}
                  <span>{casType}</span>
                </>
              )}
              {(casId || period) && (
                <>
                  <span className="cas-import-dot">·</span>
                  <span>
                    {casId ? `CAS ID ${casId}` : 'Statement'}
                    {period ? ` · ${period}` : ''}
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="cas-import-totals">
            <div>
              <div className="cas-import-total-label">Total</div>
              <div className="cas-import-total-value">{formatInr(stats.totalValue)}</div>
            </div>
            <div className="cas-import-actions">
              <button type="button" className="cas-btn" onClick={() => setJsonOpen(true)}>
                <CodeOutlined /> View JSON
              </button>
              <button type="button" className="cas-btn cas-btn-primary" onClick={() => downloadJson('imported-portfolio.json', root)}>
                <DownloadOutlined /> Export All
              </button>
              {onClear && (
                <button type="button" className="cas-btn" onClick={onClear} aria-label="Close imported portfolio">
                  <CloseOutlined />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="cas-import-summary">
          {stats.mfCount > 0 && (
            <span>
              <strong>Mutual Funds</strong> {formatInr(stats.mfCurrent)}{' '}
              <GainText value={stats.mfGain} pct={stats.mfGainPct} />
            </span>
          )}
          {stats.dematCount > 0 && (
            <span>
              <strong>Demat</strong> {formatInr(stats.dematValue)}
            </span>
          )}
          {stats.bondCount > 0 && (
            <span>
              <strong>Bonds</strong> {formatInr(stats.bondValue)}
            </span>
          )}
          {stats.npsCount > 0 && (
            <span>
              <strong>NPS</strong> {formatInr(stats.npsValue)}
            </span>
          )}
        </div>

        <div className="cas-tabs" role="tablist">
          {TABS.map((item) => {
            const count = counts[item.key];
            const empty = item.key !== 'analytics' && !count;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                className={`cas-tab${tab === item.key ? ' is-active' : ''}`}
                onClick={() => setTab(item.key)}
                disabled={empty}
              >
                {item.icon}
                {item.label}
                {empty ? <span className="cas-tab-none">None</span> : item.key !== 'analytics' && (
                  <span className="cas-tab-count">{count}</span>
                )}
                {item.beta && <span className="cas-tab-beta">Beta</span>}
              </button>
            );
          })}
        </div>

        <div className="cas-tab-body">
          <div className="cas-tab-toolbar">
            <div>
              {toolbarCopy[tab]}
              {tab === 'mf' && <GainText value={stats.mfGain} pct={stats.mfGainPct} />}
            </div>
            {tab !== 'analytics' && (
              <button type="button" className="cas-btn" onClick={() => exportCurrentTab(tab, stats, root)}>
                <DownloadOutlined /> Export
              </button>
            )}
          </div>

          {tab === 'mf' && (stats.mfRows.length ? <MutualFundsTable rows={stats.mfRows} /> : <div className="cas-empty">No mutual fund schemes in this statement.</div>)}
          {tab === 'demat' && (stats.dematRows.length ? <DematTable rows={stats.dematRows} /> : <div className="cas-empty">No demat holdings in this statement.</div>)}
          {tab === 'bonds' && (stats.bondRows.length ? (
            <BondsTable
              rows={stats.bondRows}
              onAddBond={handleAddBond}
              addingIsin={addingIsin}
              addedIsins={addedIsins}
            />
          ) : (
            <div className="cas-empty">No bonds in this statement.</div>
          ))}
          {tab === 'nps' && (stats.npsRows.length ? <NpsTable rows={stats.npsRows} /> : <div className="cas-empty">No NPS accounts in this statement.</div>)}
          {tab === 'insurance' && (stats.insuranceRows.length ? <InsuranceTable rows={stats.insuranceRows} /> : <div className="cas-empty">No insurance policies in this statement.</div>)}
          {tab === 'analytics' && <AnalyticsPanel stats={stats} />}
        </div>
      </div>

      <Modal
        title="Parsed JSON"
        open={jsonOpen}
        onCancel={() => setJsonOpen(false)}
        footer={null}
        width={840}
      >
        <pre className="cas-json-pre">{JSON.stringify(root, null, 2)}</pre>
      </Modal>
    </section>
  );
}
